// Component reference: node_modules\@nuxt\ui\dist\runtime\components\NavigationMenu.vue

import type theme from '#build/ui/navigation-menu'

// Recursively make all properties optional
type DeepPartial<T> = {
	[P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P]
}

export default {
	slots: { // Base styles for the navigation menu slots
		root: 'relative flex gap-1.5 [&>div]:min-w-0',
		list: 'isolate min-w-0',
		label: 'w-full flex items-center gap-1.5 font-semibold text-xs/5 text-highlighted px-2.5 py-1.5',
		item: 'min-w-0',
		link: 'group relative w-full flex items-center gap-1.5 font-medium text-sm before:absolute before:z-[-1] before:rounded-xs focus:outline-none focus-visible:outline-none dark:focus-visible:outline-none focus-visible:before:ring-inset focus-visible:before:ring-2',
		linkLeadingIcon: 'shrink-0 size-5',
		linkLeadingAvatar: 'shrink-0',
		linkLeadingAvatarSize: '2xs',
		linkTrailing: 'ms-auto inline-flex gap-1.5 items-center',
		linkTrailingBadge: 'shrink-0',
		linkTrailingBadgeSize: 'sm',
		linkTrailingIcon: 'size-5 transform shrink-0 group-data-[state=open]:rotate-180 transition-transform duration-200',
		linkLabel: 'truncate',
		linkLabelExternalIcon: 'inline-block size-3 align-top text-dimmed',
		childList: '',
		childItem: '',
		childLink: 'group size-full px-3 py-2 rounded-md flex items-start gap-2 text-start',
		childLinkWrapper: 'flex flex-col items-start',
		childLinkIcon: 'size-5 shrink-0',
		childLinkLabel: 'font-semibold text-sm relative inline-flex',
		childLinkLabelExternalIcon: 'inline-block size-3 align-top text-dimmed',
		childLinkDescription: 'text-sm text-muted',
		separator: 'px-2 h-px bg-(--ui-border)',
		viewportWrapper: 'absolute top-full left-0 flex w-full',
		viewport: 'relative overflow-hidden bg-default shadow-lg rounded-md ring ring-dimmed/60 h-(--reka-navigation-menu-viewport-height) w-full transition-[width,height,left] duration-200 origin-[top_center] data-[state=open]:animate-[scale-in_100ms_ease-out] data-[state=closed]:animate-[scale-out_100ms_ease-in] z-[1]',
		content: 'w-full',
		indicator: 'absolute data-[state=visible]:animate-[fade-in_100ms_ease-out] data-[state=hidden]:animate-[fade-out_100ms_ease-in] data-[state=hidden]:opacity-0 bottom-0 z-[2] w-(--reka-navigation-menu-indicator-size) translate-x-(--reka-navigation-menu-indicator-position) flex h-2.5 items-end justify-center overflow-hidden transition-[translate,width] duration-200',
		arrow: 'relative top-[50%] size-2.5 rotate-45 border border-default bg-default z-[1] rounded-xs'
	},
	variants: { // Base styles for the navigation menu variants
		color: {
			primary: {
				link: 'focus-visible:before:ring-primary',
				childLink: 'focus-visible:outline-primary'
			},
			secondary: {
				link: 'focus-visible:before:ring-accented',
				childLink: 'focus-visible:outline-accented'
			}
		},
		highlightColor: {
			primary: '',
			secondary: '',
			success: '',
			info: '',
			warning: '',
			error: '',
			neutral: ''
		},
		variant: {
			pill: '',
			link: ''
		},
		orientation: {
			horizontal: {
				root: 'items-center justify-between',
				list: 'flex items-center',
				item: 'py-2',
				link: 'px-2.5 py-1.5 before:inset-x-px before:inset-y-0',
				childList: 'grid p-2'
			},
			vertical: {
				root: 'flex-col',
				link: 'flex-row px-2.5 py-4 before:inset-y-px before:inset-x-0'
			}
		},
		contentOrientation: {
			horizontal: {
				viewportWrapper: 'justify-center',
				content: 'data-[motion=from-start]:animate-[enter-from-left_200ms_ease] data-[motion=from-end]:animate-[enter-from-right_200ms_ease] data-[motion=to-start]:animate-[exit-to-left_200ms_ease] data-[motion=to-end]:animate-[exit-to-right_200ms_ease]'
			},
			vertical: {
				viewport: 'sm:w-(--reka-navigation-menu-viewport-width) left-(--reka-navigation-menu-viewport-left)'
			}
		},
		active: { // Active states for the default nav buttons and dropdowns items
			true: {
				childLink: 'bg-elevated text-highlighted',
				childLinkIcon: 'text-highlighted'
			},
			false: {
				link: 'text-muted',
				linkLeadingIcon: 'text-inverted',
				childLink: [
					'hover:bg-elevated/50 text-default hover:text-highlighted',
					'transition-colors'
				],
				childLinkIcon: [
					'text-dimmed group-hover:text-default',
					'transition-colors'
				]
			}
		},
		disabled: {
			true: {
				link: 'cursor-not-allowed opacity-75'
			}
		},
		highlight: {
			true: ''
		},
		level: {
			true: ''
		},
		collapsed: {
			true: ''
		}
	},
	compoundVariants: [
		{ // Nav list (horizontal parent) (horizontal dropdown content)
			orientation: 'horizontal',
			contentOrientation: 'horizontal',
			class: {
				childList: 'grid-cols-2 gap-2'
			}
		},
		{ // Nav list horizontal (header)
			orientation: 'horizontal',
			class: {
				link: 'after:absolute' // "after" targets the children, "absolute" makes the dropdown items full equal height
			}
		},
		{ // Nav List vertical (footer expanded)
			orientation: 'vertical',
			collapsed: false,
			class: {
				childList: 'ms-5 border-s border-none',
				childItem: 'ps-1.5 -ms-px'
			}
		},
		{ // Pill button (primary) (active)
			color: 'primary',
			variant: 'pill',
			active: true,
			class: {
				link: 'text-primary rounded-md before:rounded-md before:bg-default hover:before:bg-accented before:transition-colors',
				linkLeadingIcon: 'text-primary group-data-[state=open]:text-primary',
				childLink: 'text-primary bg-transparent hover:bg-primary/10 before:bg-transparent outline outline-primary/30 hover:outline-none',
				childLinkIcon: 'text-primary'
			}
		},
		{ // Pill button (header parent inactive)
			disabled: false,
			active: false,
			color: 'primary',
			variant: 'pill',
			class: {
				link: 'text-default hover:text-highlighted rounded-md before:rounded-md before:bg-transparent hover:before:bg-accented data-[state=open]:bg-accented transition-colors before:transition-colors after:bg-default',
				linkLeadingIcon: 'group-data-[state=open]:text-highlighted group-hover:text-default transition-colors'
			}
		},
		{ // Pill button (footer active)
			color: 'secondary',
			variant: 'pill',
			active: true,
			class: {
				link: 'text-inverted before:bg-accented/10',
				linkLeadingIcon: 'text-inverted group-data-[state=open]:text-inverted'
			}
		},
		{ // Pill button (footer inactive)
			disabled: false,
			active: false,
			color: 'secondary',
			variant: 'pill',
			class: {
				link: 'text-inverted hover:text-inverted hover:before:bg-elevated/50 transition-colors before:transition-colors',
				linkLeadingIcon: 'group-hover:text-inverted transition-colors'
			}
		}
	],
	defaultVariants: {
		color: 'primary',
		highlightColor: 'primary',
		variant: 'pill'
	}
} satisfies DeepPartial<typeof theme>
